import * as React from 'react';

import {
  isAuthenticationResponse,
  isInvalidCredentialsError,
} from '@rematter/frontend-lib-api-client';
import { Button, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useAtom } from 'jotai';

import { useSelectOrganization } from '../hooks/mutations/useSelectOrganization';
import { $signIn } from '../state/signIn.state';

import { useTranslations } from '~/app/i18n/useTranslations';
import { config } from '~/config';
import { useIdentity } from '~/shared/hooks';
import { useTranslations as useSharedTranslations } from '~/shared/i18n/useTranslations';

export const OrganizationSelection = () => {
  const t = useTranslations();
  const ts = useSharedTranslations();
  const toast = useToast();

  const [defaultOrgId, setDefaultOrgId] = useLocalStorage('default-org-id', '');

  const [signInState, setSignInState] = useAtom($signIn);
  const [selectedOrganizationId, setSelectedOrganizationId] =
    React.useState<string>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { setAccessToken, setUser, setOrganization } = useIdentity();

  const { mutateAsync: selectOrganization, isPending } =
    useSelectOrganization();

  const handleOrganizationSelection = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      isSubmitting ||
      !signInState.pendingAuthenticationToken ||
      !selectedOrganizationId
    )
      return;

    setIsSubmitting(true);
    setDefaultOrgId(selectedOrganizationId);

    try {
      const { authenticationSelectOrganization } = await selectOrganization({
        clientId: config.workOs.clientId || '',
        pendingAuthenticationToken: signInState.pendingAuthenticationToken,
        organizationId: selectedOrganizationId,
      });

      if (isAuthenticationResponse(authenticationSelectOrganization)) {
        setSignInState({ ...signInState, authenticated: true });
        setAccessToken(authenticationSelectOrganization.accessToken);
        setUser(authenticationSelectOrganization.user);
        setOrganization(authenticationSelectOrganization.organization);
        return;
      }

      if (isInvalidCredentialsError(authenticationSelectOrganization)) {
        toast.error(t.orgSelection.errors.invalid);
      }
    } catch (err) {
      toast.error(String(err));
      // Reset to credentials step on error
      setSignInState({
        step: 'credentials',
        authenticated: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setSignInState({
      ...signInState,
      step: 'credentials',
      pendingAuthenticationToken: undefined,
    });
  };

  const handleSelectionChange = (key: string | undefined) => {
    setSelectedOrganizationId(key);
  };

  React.useEffect(() => {
    if (signInState.organizations?.some((org) => org.id === defaultOrgId)) {
      setSelectedOrganizationId(defaultOrgId);
    }
  }, [defaultOrgId, signInState.organizations]);

  const submitButtonRef = React.useRef<HTMLButtonElement>(null);

  const organizationOptions = React.useMemo(
    () =>
      signInState.organizations?.map((org) => ({
        key: org.id,
        label: org.name,
      })) ?? [],
    [signInState.organizations],
  );

  React.useLayoutEffect(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.focus();
    }
  }, [selectedOrganizationId]);

  return (
    <form
      onSubmit={handleOrganizationSelection}
      className="@OrganizationSelection flex flex-col gap-5"
    >
      <div className="flex flex-col gap-6">
        <h2 className="text-center text-xl font-semibold text-neutral/80">
          {t.orgSelection.title}
        </h2>

        <p className="text-center text-neutral">{t.orgSelection.subtitle}</p>

        <Select<string>
          placeholder={ts.auth.signIn.selectOrganization}
          options={organizationOptions}
          value={selectedOrganizationId}
          onChange={handleSelectionChange}
          defaultValue={defaultOrgId}
        />

        <div className="flex flex-col gap-3">
          <Button
            ref={submitButtonRef}
            type="submit"
            color="primary"
            variant="solid"
            isDisabled={!selectedOrganizationId}
            isLoading={isPending}
            autoFocus
          >
            {t.orgSelection.submit}
          </Button>

          <Button
            color="neutral"
            variant="outline"
            isDisabled={isSubmitting}
            onPress={handleBack}
          >
            {t.orgSelection.goBack}
          </Button>
        </div>
      </div>
    </form>
  );
};
